import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { cloneDeep } from 'lodash-es';
import { Observable, Subject, firstValueFrom, takeUntil } from 'rxjs';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { Course } from '../../../../../models/interfaces/course.interface';
import { CustomFile } from '../../../../../models/interfaces/custom_file.interface';
import { Product } from '../../../../../models/interfaces/product.interface';
import { UtilsService } from '../../../../../shared/utils/utils.service';

import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { EnfafCheckboxComponent } from '../../../../../components/enfaf-checkbox/enfaf-checkbox.component';
import { EnfafIconComponent } from '../../../../../components/enfaf-icon/enfaf-icon.component';
import { EnfafImgDraggerComponent } from '../../../../../components/enfaf-img-dragger/enfaf-img-dragger.component';
import { EnfafInputFileDraggerLightComponent } from '../../../../../components/enfaf-input-file-dragger-light/enfaf-input-file-dragger-light.component';
import { EnfafInputSuffixComponent } from '../../../../../components/enfaf-input-suffix/enfaf-input-suffix.component';
import { EnfafInputComponent } from '../../../../../components/enfaf-input/enfaf-input.component';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafSelectLightComponent } from '../../../../../components/enfaf-select-light/enfaf-select-light.component';
import { EnfafSwitchDarkComponent } from '../../../../../components/enfaf-switch-dark/enfaf-switch-dark.component';
import { EnfafTextareaLightComponent } from '../../../../../components/enfaf-textarea-light/enfaf-textarea-light.component';
import { MultiAutocompleteLightComponent, SelectOption } from '../../../../../components/multi-autocomplete-light/multi-autocomplete-light.component';
import { ProductVariant } from '../../../../../models/interfaces/product-variant.interface';
import { ProductVariantsComponent } from '../product-variants/product-variants.component';

@Component({
  selector: 'app-modal-edit-product',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    EnfafImgDraggerComponent,
    EnfafModalComponent,
    EnfafInputComponent,
    EnfafInputSuffixComponent,
    EnfafSelectLightComponent,
    EnfafSwitchDarkComponent,
    EnfafInputFileDraggerLightComponent,
    ProductVariantsComponent,
    EnfafCheckboxComponent,
    EnfafIconComponent,
    MatTooltipModule,
    EnfafTextareaLightComponent,
    MultiAutocompleteLightComponent
  ],
  templateUrl: './modal-edit-product.component.html',
  styleUrl: './modal-edit-product.component.scss'
})
export class ModalEditProductComponent implements OnInit, OnDestroy {
  productForm: FormGroup = new FormGroup({});
  availableCourses: Array<Course> = [];
  courseOptions: Array<{ value: any; viewValue: string }> = [];
  showCloseAlert = false;
  isSavingProduct = false;
  isLoadingAgreement = false;
  isLoadingAttachments = false;
  imageToUpload: File | null = null;
  imageToUploadSrc: string | null = null;
  product: Product | null = null;
  selectedCourses: SelectOption[] = [];

  // Following the course-content pattern for file handling
  currentAgreementFiles: Array<any> = [];
  fetchedAgreementUrl = false;

  // Variable to handle local agreement files (equivalent to filesToUpload in course-content)
  agreementFilesToUpload: CustomFile[] = [];

  // Variable to track when a remote file is deleted (to allow saving changes)
  isDeletingRemoteAgreement = false;

  variants: ProductVariant[] = [];

  private unsubscribe$ = new Subject<void>();
  private isComponentDestroyed = false;

  get hasReducedFields(): boolean {
    return this.productForm.get('hasReducedFields')?.value;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalEditProductComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService,
    private utils: UtilsService,
    private sanitizer: DomSanitizer,
    private iconRegistry: MatIconRegistry
  ) {
    this.availableCourses = [];
    this.courseOptions = [];

    try {
      this.iconRegistry.addSvgIcon('euro-sign', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/euro-sign.svg'));
      this.iconRegistry.addSvgIcon('percent', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/percent.svg'));
    } catch (error) {
      console.error('❌ Error registrando iconos en modal:', error);
    }
  }

  ngOnInit(): void {
    try {
      if (!this.availableCourses) {
        this.availableCourses = [];
      }

      this.currentAgreementFiles = [];
      this.fetchedAgreementUrl = false;

      this.initForm();
    } catch (error) {
      console.error('❌ Error en ngOnInit:', error);
      this.toast.presentErrorToast('Error inicializando el formulario');
      this.closeModal();
    }
  }

  ngOnDestroy(): void {
    this.isComponentDestroyed = true;
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    const receivedProduct: Product = receivedData?.product;

    this.product = receivedProduct || null;
    this.availableCourses = receivedData?.availableCourses || [];

    if (!this.availableCourses?.length) {
      this.toast.presentErrorToast('No hay cursos disponibles para editar el producto');
      this.closeModal();
      return;
    }

    this.updateCourseOptions();

    this.productForm = new FormGroup({
      productId: new FormControl(receivedProduct?.productId || null),
      name: new FormControl(receivedProduct?.name || null, [Validators.required, Validators.minLength(3), Validators.maxLength(100)]),
      price: new FormControl(receivedProduct?.price ?? 0, [Validators.required, Validators.min(0), Validators.max(10000)]),
      iva: new FormControl(receivedProduct?.price === 0 ? 0 : receivedProduct?.iva || null, [Validators.min(0), Validators.max(100)]),
      sku: new FormControl(receivedProduct?.sku || null, [
        Validators.required,
        Validators.pattern(/^[A-Z0-9_]+$/),
        Validators.minLength(3),
        Validators.maxLength(50)
      ]),
      redirectUrl: new FormControl(receivedProduct?.redirectUrl || null, [this.urlValidator.bind(this)]),
      urlGoogleSheets: new FormControl(receivedProduct?.urlGoogleSheets || null, [this.urlValidator.bind(this)]),
      isSubscription: new FormControl(receivedProduct?.isSubscription || false, Validators.required),
      frequency: new FormControl({ value: receivedProduct?.frequency || null, disabled: !receivedProduct?.isSubscription }, [
        Validators.required,
        Validators.min(1),
        Validators.max(12)
      ]),
      frequencyGranularity: new FormControl({ value: receivedProduct?.frequencyGranularity || null, disabled: !receivedProduct?.isSubscription }, [
        Validators.required
      ]),
      totalCycles: new FormControl({ value: receivedProduct?.totalCycles || null, disabled: !receivedProduct?.isSubscription }, [
        Validators.required,
        Validators.min(1),
        Validators.max(999)
      ]),
      signUpFee: new FormControl(
        { value: receivedProduct?.signUpFee === 0 ? 0 : receivedProduct?.signUpFee || null, disabled: !receivedProduct?.isSubscription },
        [Validators.required, Validators.min(0), Validators.max(10000)]
      ),
      productImageUrl: new FormControl(receivedProduct?.productImageUrl || null),
      hasVariants: new FormControl(receivedProduct?.hasVariants || false),
      variants: new FormArray([]),

      hasReducedFields: new FormControl(receivedProduct?.hasReducedFields || false),
      reducedFieldsDescription: new FormControl(receivedProduct?.reducedFieldsDescription || null)
    });

    if (receivedProduct) {
      if (receivedProduct.agreement) {
        this.mapCurrentAgreementFiles();
      }

      if (receivedProduct.variants && receivedProduct.variants.length > 0) {
        this.variants = [...receivedProduct.variants];
      }

      // Load selected courses
      if (receivedProduct.courses && receivedProduct.courses.length > 0) {
        this.loadProductCourses(receivedProduct.courses);
      }

      this.applyEditModeRestrictions();
    }

    this.listenForChanges();
  }

  // Method to map current agreement files following the course-content pattern
  async mapCurrentAgreementFiles(): Promise<void> {
    this.isLoadingAttachments = true;
    this.currentAgreementFiles = [];
    this.fetchedAgreementUrl = false;

    if (this.product?.agreement) {
      await this.fetchAgreementPresignedUrl();
    }

    this.isLoadingAttachments = false;
  }

  // Method to get presigned URL following the course-content pattern
  async fetchAgreementPresignedUrl(): Promise<void> {
    if (this.fetchedAgreementUrl) {
      return;
    }

    if (!this.product?.productId || !this.product?.agreement) {
      return;
    }

    try {
      const fileName = this.utils.getFileNameFromRemote(this.product.agreement);
      const response = await firstValueFrom(this.api.getProductAgreementPresignedUrl(this.product.productId, fileName));

      const agreementFile = {
        filename: fileName,
        path: this.product.agreement,
        presignedUrl: response.presignedUrl || response.url
      };

      this.currentAgreementFiles = [agreementFile];
    } catch (error) {
      console.error('❌ Error obteniendo URL prefirmada de agreement:', error);
      this.currentAgreementFiles = [];
    }

    this.fetchedAgreementUrl = true;
  }

  // Method to get current agreement files (equivalent to getSectionAttachments)
  getAgreementFiles(): any[] {
    return this.currentAgreementFiles || [];
  }

  // Method to get local agreement files (equivalent to filesToUpload)
  getLocalAgreementFiles(): CustomFile[] {
    return this.agreementFilesToUpload || [];
  }

  // Method to handle remote agreement file deletion
  async onDeleteRemoteAgreementAttachment(file: any): Promise<void> {
    try {
      const header = 'Eliminar archivo';
      const message = '¿Estás seguro(a) de eliminar este archivo? Esta acción no se podrá deshacer.';
      const cancel = 'Cancelar';
      const confirm = 'Eliminar';
      const deleteConfirmed = await this.alert.presentConfirmActionAlert(header, message, cancel, confirm);

      if (!deleteConfirmed) return;

      this.isDeletingRemoteAgreement = true;

      const fileIndex = this.currentAgreementFiles.findIndex((a) => a.filename === file.filename && a.path === file.path);
      if (fileIndex !== -1) {
        this.currentAgreementFiles.splice(fileIndex, 1);
      }

      this.fetchedAgreementUrl = false;
      this.productForm.markAsDirty();
      this.showCloseAlert = true;

      this.toast.presentSuccessToast('Archivo eliminado correctamente. Recuerda guardar los cambios.');
    } catch (error) {
      console.error('❌ Error eliminando archivo de agreement:', error);
      this.toast.presentErrorToast('Hubo un error eliminando el archivo');
    }
  }

  // Method to load product courses (edit mode)
  async loadProductCourses(productCourses: any[]): Promise<void> {
    if (!productCourses || productCourses.length === 0) {
      this.selectedCourses = [];
      return;
    }

    try {
      const courseIds = productCourses.map((pc: any) => pc.courseId);

      const coursesToSelect = this.availableCourses
        .filter((course) => courseIds.includes(course.courseId))
        .map((course) => ({
          id: course.courseId,
          title: course.name,
          selected: true
        }));

      this.selectedCourses = coursesToSelect;
    } catch (error) {
      console.error('Error loading product courses:', error);
      this.selectedCourses = [];
    }
  }

  // Apply edit mode restrictions
  private applyEditModeRestrictions(): void {
    // No need to disable 'courses' control anymore because we now use selectedCourses
    // which is handled directly by the multi-autocomplete

    const isSubscription = this.productForm.get('isSubscription')?.value;
    if (isSubscription) {
      this.enableSubscriptionFields();
    }
  }

  // Method to update course options once
  private updateCourseOptions(): void {
    if (!this.availableCourses || !Array.isArray(this.availableCourses)) {
      this.courseOptions = [];
      return;
    }

    this.courseOptions = this.availableCourses.map((course) => ({
      value: course.courseId,
      viewValue: course.name
    }));
  }

  // Custom URL validator
  urlValidator(control: any) {
    if (!control.value) return null;

    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    return urlPattern.test(control.value) ? null : { invalidUrl: true };
  }

  listenForChanges(): void {
    this.productForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.showCloseAlert = true;
    });

    this.productForm.controls['price'].valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      if (value === 0) {
        this.productForm.controls['iva'].setValue(0);
        this.productForm.controls['isSubscription'].setValue(false);
        this.disableSubscriptionFields();
      }
    });

    this.productForm.controls['isSubscription'].valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      const currentPrice = this.productForm.get('price')?.value;

      if (currentPrice === 0 && value === true) {
        this.productForm.controls['isSubscription'].setValue(false);
        this.disableSubscriptionFields();
        return;
      }

      if (value) {
        this.enableSubscriptionFields();
      } else {
        this.disableSubscriptionFields();
      }
    });
  }

  enableSubscriptionFields(): void {
    this.productForm.controls['frequency'].enable({ emitEvent: false });
    this.productForm.controls['frequencyGranularity'].enable({ emitEvent: false });
    this.productForm.controls['totalCycles'].enable({ emitEvent: false });
    this.productForm.controls['signUpFee'].enable({ emitEvent: false });

    this.productForm.controls['frequency'].setValidators([Validators.required, Validators.min(1), Validators.max(12)]);
    this.productForm.controls['frequencyGranularity'].setValidators([Validators.required]);
    this.productForm.controls['totalCycles'].setValidators([Validators.required, Validators.min(1), Validators.max(999)]);
    this.productForm.controls['signUpFee'].setValidators([Validators.required, Validators.min(0), Validators.max(10000)]);

    this.productForm.controls['frequency'].updateValueAndValidity();
    this.productForm.controls['frequencyGranularity'].updateValueAndValidity();
    this.productForm.controls['totalCycles'].updateValueAndValidity();
    this.productForm.controls['signUpFee'].updateValueAndValidity();

    // Mark form as dirty to enable save button
    this.productForm.markAsDirty();
  }

  disableSubscriptionFields(): void {
    this.productForm.controls['frequency'].disable({ emitEvent: false });
    this.productForm.controls['frequencyGranularity'].disable({ emitEvent: false });
    this.productForm.controls['totalCycles'].disable({ emitEvent: false });
    this.productForm.controls['signUpFee'].disable({ emitEvent: false });

    this.productForm.controls['frequency'].setValue(null);
    this.productForm.controls['frequencyGranularity'].setValue(null);
    this.productForm.controls['totalCycles'].setValue(null);
    this.productForm.controls['signUpFee'].setValue(null);

    this.productForm.controls['frequency'].setValidators([Validators.min(1), Validators.max(12)]);
    this.productForm.controls['frequencyGranularity'].setValidators(null);
    this.productForm.controls['totalCycles'].setValidators([Validators.min(1), Validators.max(999)]);
    this.productForm.controls['signUpFee'].setValidators([Validators.min(0), Validators.max(10000)]);

    this.productForm.controls['frequency'].updateValueAndValidity();
    this.productForm.controls['frequencyGranularity'].updateValueAndValidity();
    this.productForm.controls['totalCycles'].updateValueAndValidity();
    this.productForm.controls['signUpFee'].updateValueAndValidity();

    // Mark form as dirty to enable save button
    this.productForm.markAsDirty();
  }

  // Method to handle files when uploaded (equivalent to onDroppedFiles)
  onDroppedFiles(files: Array<CustomFile>): void {
    if (!files?.length) return;

    const file = files[0];

    if (!file.file.type.includes('pdf')) {
      this.toast.presentErrorToast('Solo se permiten archivos PDF para el contrato');
      return;
    }

    file.name = this.utils.generateFileName(file.name);
    this.agreementFilesToUpload = [file];

    if (this.currentAgreementFiles.length > 0) {
      this.currentAgreementFiles = [];
      this.fetchedAgreementUrl = false;
    }

    this.isDeletingRemoteAgreement = false;
    this.toast.presentSuccessToast('Archivo PDF agregado correctamente');
  }

  // Method to update local files (equivalent to onLocalFilesUpdated)
  onAgreementLocalFilesUpdated(files: CustomFile[]): void {
    this.agreementFilesToUpload = files || [];
  }

  // Method to delete local file (used by file upload component)
  deleteAgreementFile(fileResult: any): void {
    this.agreementFilesToUpload = [];
    this.toast.presentSuccessToast('Archivo eliminado correctamente');
  }

  // Compatibility method for deleting remote files from template
  deleteCurrentAgreementFile(file: any): void {
    this.onDeleteRemoteAgreementAttachment(file);
  }

  async saveProduct(): Promise<void> {
    const hasVariantsEnabled = this.productForm.get('hasVariants')?.value;
    if (hasVariantsEnabled) {
      const variantsFormArray = this.productForm.get('variants') as FormArray;
      if (variantsFormArray.length === 0 || variantsFormArray.invalid) {
        this.markFormGroupTouched(this.productForm);
        this.markFormArrayTouched(variantsFormArray);
        return;
      }
    }

    if (this.productForm.invalid && !this.isDeletingRemoteAgreement && this.agreementFilesToUpload.length === 0) {
      this.markFormGroupTouched(this.productForm);
      return;
    }

    try {
      this.isSavingProduct = true;
      const productFormData = new FormData();
      const body = this.productForm.getRawValue();

      if (body.price === 0) {
        body.isSubscription = false;
      }

      if (body.hasVariants && this.variants.length > 0) {
        body.variants = this.variants;
      } else {
        body.hasVariants = false;
        body.variants = [];
      }

      // Usar selectedCourses en lugar del form control
      body.courses = this.selectedCourses.map((course) => course.id);

      if (body.signUpFee !== null && body.signUpFee !== undefined) {
        body.signUpFee = Number(body.signUpFee);
      }

      productFormData.append('form', JSON.stringify(body));

      if (this.agreementFilesToUpload?.length > 0) {
        const agreementFile = this.agreementFilesToUpload[0];
        if (agreementFile.file) {
          productFormData.append('agreement', agreementFile.file, agreementFile.name);
        }
      }

      if (this.imageToUpload) {
        productFormData.append('productImage', this.imageToUpload, this.imageToUpload.name);
      }

      const response = await firstValueFrom(this.api.updateProduct(productFormData));

      let successMessage: string;
      if (this.selectedCourses.length === 0) {
        successMessage = `El producto "${body.name}" se ha actualizado correctamente sin cursos asociados`;
      } else if (this.selectedCourses.length === 1) {
        successMessage = `El producto "${body.name}" para el curso "${this.selectedCourses[0].title}" se ha actualizado correctamente`;
      } else {
        successMessage = `El producto "${body.name}" se ha actualizado correctamente con ${this.selectedCourses.length} cursos seleccionados`;
      }

      this.isDeletingRemoteAgreement = false;
      this.agreementFilesToUpload = [];

      this.isSavingProduct = false;
      this.closeModal({ productUpdated: response.productUpdated });
      this.toast.presentSuccessToast(successMessage);
    } catch (errorResponse: any) {
      this.isSavingProduct = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error actualizando el producto');
    }
  }

  uploadImage(data: { imageToUpload: File | null; imageToUploadSrc: string | null }): void {
    this.imageToUpload = data.imageToUpload;
    this.imageToUploadSrc = data.imageToUploadSrc;
  }

  deleteImage(): void {
    this.imageToUpload = null;
    this.imageToUploadSrc = null;
    this.productForm.patchValue({
      productImageUrl: null
    });
    this.productForm.get('productImageUrl')?.updateValueAndValidity();
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        this.markFormArrayTouched(control);
      }
    });
  }

  private markFormArrayTouched(formArray: FormArray): void {
    formArray.controls.forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        this.markFormArrayTouched(control);
      }
    });
  }

  get shouldDisableConfirmButton(): boolean {
    if (this.isSavingProduct) {
      return true;
    }

    const hasVariantsEnabled = this.productForm.get('hasVariants')?.value;
    if (hasVariantsEnabled) {
      const variantsFormArray = this.productForm.get('variants') as FormArray;

      if (variantsFormArray.length === 0) {
        return true;
      }

      if (variantsFormArray.invalid) {
        return true;
      }
    }

    // If deleting a remote file, allow saving
    if (this.isDeletingRemoteAgreement) {
      return false;
    }

    // If there are local files to upload, allow saving
    if (this.agreementFilesToUpload.length > 0) {
      return false;
    }

    // If there's an image to upload, allow saving
    if (this.imageToUpload) {
      return false;
    }

    const hasVariantChanges = this.hasVariantsChanged();
    if (hasVariantChanges) {
      return false;
    }

    const hasCoursesChanges = this.hasCoursesChanged();
    if (hasCoursesChanges) {
      return false;
    }

    const hasRealFormChanges = this.hasFormChangedFromInitial();
    if (!hasRealFormChanges) {
      return true;
    }

    // If there are form changes, verify if it's valid
    // Only consider enabled fields for validation
    const enabledControls = Object.keys(this.productForm.controls).filter((key) => {
      const control = this.productForm.get(key);
      return control && !control.disabled && key !== 'variants';
    });

    const hasValidationErrors = enabledControls.some((key) => {
      const control = this.productForm.get(key);
      return control && control.invalid;
    });

    return hasValidationErrors;
  }

  get nameErrors() {
    const control = this.productForm.get('name');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'El nombre del producto es requerido';
      if (control.errors['minlength']) return 'El nombre debe tener al menos 3 caracteres';
      if (control.errors['maxlength']) return 'El nombre no puede exceder 100 caracteres';
    }
    return null;
  }

  get priceErrors() {
    const control = this.productForm.get('price');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'El precio es requerido';
      if (control.errors['min']) return 'El precio mínimo es 0';
      if (control.errors['max']) return 'El precio máximo es 10,000';
    }
    return null;
  }

  get ivaErrors() {
    const control = this.productForm.get('iva');
    if (control?.errors && control.touched) {
      if (control.errors['min']) return 'El IVA mínimo es 0%';
      if (control.errors['max']) return 'El IVA máximo es 100%';
    }
    return null;
  }

  get skuErrors() {
    const control = this.productForm.get('sku');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'El SKU es requerido';
      if (control.errors['pattern']) return 'El SKU solo puede contener letras mayúsculas, números y guiones bajos';
      if (control.errors['minlength']) return 'El SKU debe tener al menos 3 caracteres';
      if (control.errors['maxlength']) return 'El SKU no puede exceder 50 caracteres';
    }
    return null;
  }

  get redirectUrlErrors() {
    const control = this.productForm.get('redirectUrl');
    if (control?.errors && control.touched) {
      if (control.errors['invalidUrl']) return 'La URL de redirección no es válida';
    }
    return null;
  }

  get urlGoogleSheetsErrors() {
    const control = this.productForm.get('urlGoogleSheets');
    if (control?.errors && control.touched) {
      if (control.errors['invalidUrl']) return 'La URL de callback no es válida';
    }
    return null;
  }

  get frequencyErrors() {
    const control = this.productForm.get('frequency');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'La frecuencia de cobro es requerida';
      if (control.errors['min'] || control.errors['max']) return 'La frecuencia debe estar entre 1 y 12';
    }
    return null;
  }

  get frequencyGranularityErrors() {
    const control = this.productForm.get('frequencyGranularity');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'La granularidad de la frecuencia es requerida';
    }
    return null;
  }

  get totalCyclesErrors() {
    const control = this.productForm.get('totalCycles');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'El número de ciclos es requerido';
      if (control.errors['min'] || control.errors['max']) return 'El número de ciclos debe estar entre 1 y 999';
    }
    return null;
  }

  onVariantsChange(variants: ProductVariant[]): void {
    this.variants = variants;
    this.showCloseAlert = true;
  }

  private hasCoursesChanged(): boolean {
    if (!this.product) {
      return this.selectedCourses.length > 0;
    }

    const originalCourses = this.product.courses || [];
    const originalCourseIds = originalCourses.map((c: any) => c.courseId);
    const currentCourseIds = this.selectedCourses.map((c) => c.id);

    // If lengths are different, there are changes
    if (originalCourseIds.length !== currentCourseIds.length) {
      return true;
    }

    // Check if all original IDs are in current ones
    for (const originalId of originalCourseIds) {
      if (!currentCourseIds.includes(originalId)) {
        return true;
      }
    }

    // Check if all current IDs are in original ones
    for (const currentId of currentCourseIds) {
      if (!originalCourseIds.includes(currentId)) {
        return true;
      }
    }

    return false;
  }

  private hasVariantsChanged(): boolean {
    if (!this.product) {
      return this.variants.length > 0;
    }

    const originalVariants = this.product.variants || [];

    if (originalVariants.length !== this.variants.length) {
      return true;
    }

    for (let i = 0; i < this.variants.length; i++) {
      const currentVariant = this.variants[i];
      const originalVariant = originalVariants.find((v) => v.productVariantId === currentVariant.productVariantId);

      if (!originalVariant) {
        return true;
      }

      if (originalVariant.name !== currentVariant.name || originalVariant.price !== currentVariant.price) {
        return true;
      }
    }

    for (const originalVariant of originalVariants) {
      const exists = this.variants.some((v) => v.productVariantId === originalVariant.productVariantId);
      if (!exists) {
        return true;
      }
    }

    return false;
  }

  private hasFormChangedFromInitial(): boolean {
    if (!this.product) {
      const formValue = this.productForm.value;
      return !!(
        formValue.name ||
        formValue.price !== 0 ||
        formValue.sku ||
        formValue.redirectUrl ||
        formValue.urlGoogleSheets ||
        formValue.isSubscription ||
        formValue.hasVariants ||
        formValue.hasReducedFields ||
        formValue.reducedFieldsDescription
      );
    }

    const currentValues = this.productForm.getRawValue();
    const originalProduct = this.product;

    const fieldsToCheck = [
      'name',
      'price',
      'iva',
      'sku',
      'redirectUrl',
      'urlGoogleSheets',
      'isSubscription',
      'frequency',
      'frequencyGranularity',
      'totalCycles',
      'signUpFee',
      'hasVariants',
      'hasReducedFields',
      'reducedFieldsDescription'
    ];

    return fieldsToCheck.some((field) => {
      const currentValue = currentValues[field];
      const originalValue = originalProduct[field as keyof Product];

      if (field === 'iva' && originalProduct.price === 0) {
        return currentValue !== 0;
      }

      if (field === 'signUpFee' && (originalValue === 0 || originalValue === null)) {
        return currentValue !== 0 && currentValue !== null;
      }

      return currentValue !== originalValue;
    });
  }

  get signUpFeeErrors() {
    const control = this.productForm.get('signUpFee');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'El costo de inscripción es requerido';
      if (control.errors['min']) return 'El costo de inscripción mínimo es 0';
      if (control.errors['max']) return 'El costo de inscripción máximo es 10,000';
    }
    return null;
  }

  fetchCourses = (search: string, page: number, pageSize: number): Observable<{ data: SelectOption[]; total: number }> => {
    return new Observable((observer) => {
      firstValueFrom(
        // ENF-2372: a Máster goes in a product by edition, with its real name (its root is «… - 1ª Edición»).
        this.api.fetchAllCourses(false, {
          page: page,
          pageSize,
          search
        })
      )
        .then((response) => {
          const mappedData: SelectOption[] =
            response.courses?.map((course: any) => ({
              id: course.courseId,
              title: course.name,
              selected: false
            })) || [];

          observer.next({
            data: mappedData,
            total: response.pagination?.totalItems || mappedData.length
          });
          observer.complete();
        })
        .catch((error) => {
          if (this.isComponentDestroyed) {
            observer.complete();
            return;
          }
          console.error('Error fetching courses:', error);
          observer.next({
            data: [],
            total: 0
          });
          observer.complete();
        });
    });
  };

  onSelectedCoursesChanged(selected: SelectOption[]): void {
    this.selectedCourses = selected;
  }

  removeCourse(courseId: string): void {
    this.selectedCourses = this.selectedCourses.filter((c) => c.id !== courseId);
  }
}
